import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import marketingIllustration from '../assets/images/5.png';
import ProposalForm from "./ProposalForm";

const BRAND_BLUE = "#0047ff";
const BRAND_GRADIENT = "linear-gradient(135deg, #0047ff, #00c4ff)";

const Wrapper = styled.div`
  font-family: 'Poppins', sans-serif;
  min-height: 100vh;
  color: #e5e7eb;
  padding: 80px 20px;
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
  position: relative;
`;

const Hero = styled(motion.section)`
  text-align: center;
  padding: 40px 20px;

  @media (max-width: 360px) {
    padding: 30px 14px;  
  }
`;

const HeroTitle = styled.h1`
  margin-bottom: 20px;
  font-size: clamp(2rem, 6vw, 2.8rem);
  font-weight: 700;
  background: ${BRAND_GRADIENT};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-align: center;
`;

const HeroText = styled.p`
  max-width: 800px;
  margin: 0 auto;
  font-size: 1.1rem;
  line-height: 1.7;
  color: #cbd5e1;
`;

const CtaButton = styled(motion.button)`
  margin-top: 30px;
  padding: 14px 36px;
  border-radius: 50px;
  border: none;
  background: ${BRAND_GRADIENT};
  color: white;
  font-weight: 600;
  font-size: 1.05rem;
  cursor: pointer;
`;

const WhySection = styled.section`
  display: flex;
  flex-direction: column;   
  align-items: center;
  text-align: center;
  gap: 24px;
  max-width: 900px;
  width: 100%;
  margin: 60px auto;
  padding: 0 16px;
`;

const WhyText = styled.div`
  max-width: 700px;
`;

const WhyTitle = styled.h2`
  font-size: 2rem;
  margin-bottom: 16px;
  background: ${BRAND_GRADIENT};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const WhyPara = styled.p`
  font-size: 1.05rem;
  line-height: 1.7;
  color: #cbd5e1;
`;

const WhyImage = styled.img`
  width: 100%;
  max-width: 320px;
  height: auto;
  border-radius: 20px;
  display: block;
  margin: 0 auto;
`;

const FeaturesGrid = styled.section`
  display: flex;
  flex-wrap: wrap;
  gap: 30px;
  justify-content: center;
  padding-top: 60px;
`;

const FeatureBox = styled(motion.div)`
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(14px);
  border-radius: 18px;
  padding: 26px;
  max-width: 320px;
  text-align: center;
  box-shadow: 0 10px 30px rgba(0,0,0,0.25);
  border: 1px solid rgba(255,255,255,0.12);
`;

const FeatureTitle = styled.h3`
  font-size: 1.25rem;
  margin-bottom: 10px;
  background: ${BRAND_GRADIENT};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const FeatureDesc = styled.p`
  font-size: 0.95rem;
  line-height: 1.6;
  color: #cbd5e1;
`;

export default function SocialMediaMarketing() {

  const [showProposal, setShowProposal] = useState(false);

  return (
    <>
      <Wrapper>

        <Hero
          initial={{ opacity: 0, y: -40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: 'easeOut' }}
        >
          <HeroTitle>Strategic Social Media Marketing Company</HeroTitle>
          <HeroText>
            Grow your brand visibility, engagement, and conversions with our expert 
            social media marketing services. We create data-driven strategies, 
            compelling content, and performance-focused campaigns that help your 
            business stand out in the digital landscape.
          </HeroText>

          <CtaButton
            whileHover={{ scale: 1.08, boxShadow: '0 0 16px rgba(0,86,210,.3)' }}
            whileTap={{ scale: 0.96 }}
            transition={{ type: 'spring', stiffness: 260, damping: 18 }}
            onClick={() => setShowProposal(true)}
          >
            Request a Proposal
          </CtaButton>
        </Hero>

        <WhySection>
          <WhyText>
            <WhyTitle>Why Invest In Social Media Marketing?</WhyTitle>
            <WhyPara>
              Social media is where your customers spend their time. Strategic 
              social media marketing builds brand awareness, strengthens customer 
              relationships, and drives measurable business growth. With the right 
              strategy, content, and analytics, your brand can turn followers into 
              loyal customers.
            </WhyPara>
          </WhyText>

          <WhyImage
            src={marketingIllustration}
            alt="Social Media Marketing Illustration"
          />
        </WhySection>

        <FeaturesGrid>
          {features.map((feature) => (
            <FeatureBox
              key={feature.title}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              whileHover={{
                y: -12,
                scale: 1.05,
                boxShadow: "0 0 35px rgba(0,196,255,0.35)",
              }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
            >
              <FeatureTitle>{feature.title}</FeatureTitle>
              <FeatureDesc>{feature.description}</FeatureDesc>
            </FeatureBox>
          ))}
        </FeaturesGrid>

      </Wrapper>

      <ProposalForm open={showProposal} setOpen={setShowProposal} />
    </>
  );
}

const features = [
  {
    title: 'Social Media Strategy Development',
    description:
      'We create customized strategies aligned with your business goals to maximize reach and engagement.',
  },
  {
    title: 'Content Creation & Management',
    description:
      'Engaging posts, reels, stories, and creatives designed to capture attention and drive interaction.',
  },
  {
    title: 'Paid Advertising Campaigns',
    description:
      'Targeted ad campaigns on platforms like Facebook, Instagram, and LinkedIn for maximum ROI.',
  },
  {
    title: 'Performance Analytics & Reporting',
    description:
      'Data-driven insights to track performance and continuously optimize your campaigns.',
  },
  {
    title: 'Brand Engagement & Community Building',
    description:
      'We help you build a loyal online community through consistent engagement and interaction.',
  },
  {
    title: 'Influencer Marketing Support',
    description:
      'Collaborations with relevant influencers to expand brand reach and credibility.',
  },
];